import {
  ContentTransformer,
  ContentTransformerNode,
  NodeContent,
  type NodeProps,
  type Overrides,
} from "@crystallize/reactjs-components";

/**
 * Block-level node types that are not allowed inside a <p>.
 */
const BLOCK_TYPES = new Set([
  "paragraph",
  "unordered-list",
  "ordered-list",
  "list",
  "quote",
  "preformatted",
  "heading1",
  "heading2",
  "heading3",
  "heading4",
  "heading5",
  "heading6",
  "horizontal-line",
  "table",
  "div",
]);

const isBlock = (node?: NodeProps) =>
  !!node && node.kind === "block" && BLOCK_TYPES.has(node.type ?? "");

/**
 * Crystallize rich text sometimes hands us a `paragraph` node whose children
 * include block content - a list, a heading. The library renders paragraph as
 * <p>, so those come out as <ul>/<h3> inside <p>, which is invalid HTML: the
 * browser hoists them out while parsing, the DOM then disagrees with what
 * React rendered, and we get a hydration error.
 *
 * Split such a paragraph rather than nesting it. Runs of inline children keep
 * their <p> - and so keep the paragraph styling in index.css - and block
 * children are emitted as siblings, which is what the content actually means.
 */
const overrides: Overrides = {
  paragraph: (props) => {
    const children = props.children ?? [];

    if (!children.some(isBlock)) {
      return (
        <p>
          <NodeContent {...props} />
        </p>
      );
    }

    const out: React.ReactNode[] = [];
    let run: NodeProps[] = [];

    const flushRun = (key: string) => {
      if (run.length === 0) return;
      const inline = run;
      run = [];
      out.push(
        <p key={`p-${key}`}>
          {inline.map((child, i) => (
            <ContentTransformerNode
              key={i}
              {...child}
              overrides={props.overrides}
            />
          ))}
        </p>,
      );
    };

    children.forEach((child, i) => {
      if (isBlock(child)) {
        flushRun(String(i));
        out.push(
          <ContentTransformerNode
            key={`b-${i}`}
            {...child}
            overrides={props.overrides}
          />,
        );
      } else {
        run.push(child);
      }
    });
    flushRun("tail");

    return <>{out}</>;
  },
};

/**
 * ContentTransformer with the project's shared overrides applied. Prefer this
 * over using ContentTransformer directly.
 */
export const RichTextRenderer: React.FC<{ json?: NodeProps[] | NodeProps }> = ({
  json,
}) => <ContentTransformer json={json} overrides={overrides} />;
